/**
 * Service Detail Route
 *
 * Displays comprehensive service metadata with edit/delete actions
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useService } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  ErrorBoundary,
  DeleteConfirmationDialog,
  PermissionGate,
  ExtensionDataBadge,
} from '../../components/shared';
import {
  ServiceBasicInfoCard,
  ServiceTechnicalDetailsCard,
  ServiceMetadataCard,
  ServiceOwnershipSection,
  ServiceTechnologiesSection,
  ServiceProjectAssignmentsSection,
  ScanLinkBadge,
  ScanLinkCallToAction,
} from '../../components/services';
import { ServiceTagsAndCustomFields } from '../../components/services/ServiceTagsAndCustomFields';
import { deleteService, ApiError } from '../../lib/apiClient';
import type { ServiceTechnologyAssignment } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';
import '@tylertech/forge/text-field';
import './serviceId.css';

export const Route = createFileRoute('/services/$serviceId')({
  component: ServiceDetailPage,
});

const BACK_TO_SERVICES_SEARCH = {
  page: 1,
  pageSize: 25,
  search: '',
  lifecycleStatus: '' as const,
  tier: '' as const,
  tagId: '',
  teamId: '',
  technologyId: '',
};

function ServiceDetailPage(): JSX.Element {
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if we're on a child route
  const isEditRoute = location.pathname.endsWith('/edit');
  const isDependenciesRoute = location.pathname.endsWith('/dependencies');
  const isImpactRoute = location.pathname.endsWith('/impact');
  const isDiagramsRoute = location.pathname.endsWith('/diagrams');
  const isAssessmentsRoute = location.pathname.includes('/assessments');
  const isChildRoute =
    isEditRoute || isDependenciesRoute || isImpactRoute || isDiagramsRoute || isAssessmentsRoute;

  // Fetch service data
  const { data, loading, error: fetchError } = useService(serviceId);
  const service = data?.service;

  // Technology assignments already made to this service, with edge
  // metadata (`notes`, pinned `versionId`/`version`) — the
  // `technologyAssignments` field (issue #1284) rather than the flat
  // `technologies` list, so the assigned-version can be displayed per row.
  const assignedTechnologyAssignments: ServiceTechnologyAssignment[] =
    data?.service?.technologyAssignments ?? [];

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  // Loading state
  if (loading) {
    return (
      <div className="service-detail-page">
        <DetailSkeleton cards={4} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="service-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load service"
          description={fetchError.message}
          actionLabel="Back to Services"
          onAction={() => navigate({ to: '/services', search: BACK_TO_SERVICES_SEARCH })}
        />
      </div>
    );
  }

  // Not found state
  if (!service) {
    return (
      <div className="service-detail-page">
        <EmptyState
          icon="search_off"
          title="Service not found"
          description={`No service found with ID: ${serviceId}`}
          actionLabel="Back to Services"
          onAction={() => navigate({ to: '/services', search: BACK_TO_SERVICES_SEARCH })}
        />
      </div>
    );
  }

  // Handle assessments action
  const handleAssessments = (): void => {
    navigate({ to: `/services/${service.entityId}/assessments` as string });
  };

  // Handle dependencies action
  const handleDependencies = (): void => {
    navigate({
      to: '/services/$serviceId/dependencies',
      params: { serviceId: service.entityId },
    });
  };

  // Handle impact analysis action
  const handleImpactAnalysis = (): void => {
    navigate({ to: `/services/${service.entityId}/impact` as string });
  };

  // Handle diagrams action
  const handleDiagrams = (): void => {
    navigate({ to: `/services/${service.entityId}/diagrams` as string });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({ to: `/services/${service.entityId}/edit` as string });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteService(serviceId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListServices'],
      });

      // Navigate back to services list
      navigate({ to: '/services', search: BACK_TO_SERVICES_SEARCH });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Cancel delete
  const handleCancelDelete = (): void => {
    setDeleteDialogOpen(false);
  };

  // If on a child route, just render the outlet
  if (isChildRoute) {
    return (
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="service-detail-page">
        {/* Breadcrumb Navigation */}
        <div className="breadcrumb">
          <Link to="/services" search={BACK_TO_SERVICES_SEARCH} className="breadcrumb-link">
            <IconElement name="arrow_back" aria-hidden="true" />
            <span>Back to Services</span>
          </Link>
        </div>

        {/* Error Banner */}
        {error && (
          <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
            <div className="banner-content">
              <IconElement name="error" aria-hidden="true" />
              <span>{error}</span>
            </div>
          </BannerElement>
        )}

        {/* Header */}
        <div className="service-header">
          <div className="service-header-content">
            <h1 className="forge-typography--headline4">{service.name}</h1>
            {service.shortName && (
              <p className="forge-typography--body2 service-short-name">{service.shortName}</p>
            )}
            <ExtensionDataBadge extensionData={service._extensionData} />
            <ScanLinkBadge service={service} />
          </div>
          <div className="service-actions">
            <ForgeButton type="button" variant="outlined" onClick={handleAssessments}>
              <forge-icon name="assignment" aria-hidden="true"></forge-icon>
              <span>Assessments</span>
            </ForgeButton>
            <ForgeButton type="button" variant="outlined" onClick={handleDependencies}>
              <forge-icon name="account_tree" aria-hidden="true"></forge-icon>
              <span>Dependencies</span>
            </ForgeButton>
            <ForgeButton type="button" variant="outlined" onClick={handleImpactAnalysis}>
              <forge-icon name="warning" aria-hidden="true"></forge-icon>
              <span>Impact</span>
            </ForgeButton>
            <ForgeButton type="button" variant="outlined" onClick={handleDiagrams}>
              <forge-icon name="architecture" aria-hidden="true"></forge-icon>
              <span>Diagrams</span>
            </ForgeButton>
            <PermissionGate permission="service:update">
              <ForgeButton type="button" variant="outlined" onClick={handleEdit}>
                <forge-icon name="edit" aria-hidden="true"></forge-icon>
                <span>Edit</span>
              </ForgeButton>
            </PermissionGate>
            <PermissionGate permission="service:delete">
              <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
                <forge-icon name="delete" aria-hidden="true"></forge-icon>
                <span>Delete</span>
              </ForgeButton>
            </PermissionGate>
          </div>
        </div>

        <ScanLinkCallToAction service={service} />

        {/* Service Details Cards */}
        <div className="service-content">
          <ServiceBasicInfoCard service={service} />
          <ServiceOwnershipSection service={service} />
          <ServiceTechnicalDetailsCard service={service} />

          {/* Tags & Custom Fields Card (combined; issue #746) */}
          <ServiceTagsAndCustomFields serviceId={service.entityId} />

          <ServiceMetadataCard service={service} />
        </div>

        {/* Technologies Card */}
        <ServiceTechnologiesSection
          serviceId={serviceId}
          assignedTechnologyAssignments={assignedTechnologyAssignments}
        />

        {/* Project Assignments */}
        <ServiceProjectAssignmentsSection serviceId={serviceId} />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title="Delete Service"
          message="Are you sure you want to delete this service?"
          itemName={service.name}
          warning="This action will soft delete the service. It can be recovered by an administrator."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDeleting={isDeleting}
        />
      </div>
    </ErrorBoundary>
  );
}

// Export for testing
export { ServiceDetailPage };
